import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, UserPlus, ShoppingBag, Truck, ShieldAlert } from 'lucide-react';

type Role = 'customer' | 'delivery' | 'super_user';

export default function Signup() {
  const navigate = useNavigate();
  const [role, setRole] = useState<Role>('customer');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    fullName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
    } else if (fullName.trim().length < 2) {
      newErrors.fullName = 'Full Name must be at least 2 characters';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.register({
        fullName,
        email,
        password,
        phone,
        role,
      });
      localStorage.setItem('token', response.data.accessToken);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      let welcomeMsg = 'Welcome to GrocerySub!';
      if (role === 'delivery') welcomeMsg = 'Welcome to the Delivery Team!';
      if (role === 'super_user') welcomeMsg = 'Welcome Admin!';

      toast.success(welcomeMsg);
      navigate('/');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const roleStyles = {
    customer: {
      bgGradient: 'from-green-50 to-green-100',
      text: 'text-green-600',
      ring: 'focus:ring-green-500',
      btn: 'bg-green-600 hover:bg-green-700',
      border: 'border-green-600',
      bgSelect: 'bg-green-50',
    },
    delivery: {
      bgGradient: 'from-blue-50 to-blue-100',
      text: 'text-blue-600',
      ring: 'focus:ring-blue-500',
      btn: 'bg-blue-600 hover:bg-blue-700',
      border: 'border-blue-600',
      bgSelect: 'bg-blue-50',
    },
    super_user: {
      bgGradient: 'from-purple-50 to-purple-100',
      text: 'text-purple-600',
      ring: 'focus:ring-purple-500',
      btn: 'bg-purple-600 hover:bg-purple-700',
      border: 'border-purple-600',
      bgSelect: 'bg-purple-50',
    }
  };

  const styles = roleStyles[role];

  return (
    <div className={`min-h-screen bg-gradient-to-br ${styles.bgGradient} flex items-center justify-center p-4 transition-colors duration-500`}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">

        {/* Role Selection Header */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setRole('customer')}
            className={`flex-1 py-4 text-sm font-medium flex flex-col items-center gap-1 transition-colors ${role === 'customer' ? `${styles.text} border-b-2 ${styles.border} ${styles.bgSelect}` : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <ShoppingBag size={20} />
            Customer
          </button>
          <button
            type="button"
            onClick={() => setRole('delivery')}
            className={`flex-1 py-4 text-sm font-medium flex flex-col items-center gap-1 transition-colors ${role === 'delivery' ? `text-blue-600 border-b-2 border-blue-600 bg-blue-50` : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <Truck size={20} />
            Delivery
          </button>
          <button
            type="button"
            onClick={() => setRole('super_user')}
            className={`flex-1 py-4 text-sm font-medium flex flex-col items-center gap-1 transition-colors ${role === 'super_user' ? `text-purple-600 border-b-2 border-purple-600 bg-purple-50` : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <ShieldAlert size={20} />
            Admin
          </button>
        </div>

        <div className="p-8">
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold ${styles.text} transition-colors duration-300`}>
              {role === 'customer' && 'Create Account'}
              {role === 'delivery' && 'Join Delivery Team'}
              {role === 'super_user' && 'Admin Access'}
            </h1>
            <p className="text-gray-500 mt-2">
              {role === 'customer' && 'Start shopping for groceries today'}
              {role === 'delivery' && 'Register to start delivering orders'}
              {role === 'super_user' && 'Register for platform management'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  setErrors({ ...errors, fullName: '' });
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${styles.ring} focus:border-transparent ${errors.fullName ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your full name"
                required
              />
              {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors({ ...errors, email: '' });
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${styles.ring} focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your email"
                required
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone (optional)
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 ${styles.ring} focus:border-transparent`}
                placeholder="Enter your phone number"
              />
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors({ ...errors, password: '' });
                    if (confirmPassword && e.target.value === confirmPassword) {
                      setErrors({ ...errors, password: '', confirmPassword: '' });
                    }
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${styles.ring} focus:border-transparent ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Create a password (min 6 characters)"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrors({ ...errors, confirmPassword: '' });
                  if (password && e.target.value !== password) {
                    setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
                  } else if (e.target.value === password) {
                    setErrors({ ...errors, confirmPassword: '' });
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${styles.ring} focus:border-transparent ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Confirm your password"
                required
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full ${styles.btn} text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              {loading ? (
                'Creating account...'
              ) : (
                <>
                  <UserPlus size={20} />
                  Sign Up
                </>
              )}
            </button>
          </form>

          <p className="text-center mt-6 text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className={`${styles.text} font-semibold hover:underline`}>
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}